import React, { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  shouldRedirectToActivation,
  allowAppsumoRedirect,
  leavesActivationAlone,
  ACTIVATE_PATH as APPSUMO_ACTIVATE_PATH,
} from "./services/appsumo";
import * as Views from "./views";
import * as Components from "./components";
import * as Mixpanel from "./services/mixpanel";
import LoggedIn from "./views/auth/logged-in";
import AdminRoutes from "./admin";
import LinkedInCallbackHandler from "./views/auth/linkedin-callback-handler";
import { Loading, ChunkErrorBoundary } from "./components/common";
import { getStoredWorkspace, storeActiveWorkspace } from "./api/workspace";
import { NotificationContext } from "./context/notification-context";
import { clearChunkReloadFlag } from "./utils/lazy-with-retry";
import { isContentRadarHost } from "./utils/content-radar";

const App = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Clear chunk reload flag on successful app mount
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  // A buyer who arrived from AppSumo and had to sign up first is sent on to
  // onboarding by the post-login redirect; the pending token brings them back
  // to finish activation. Auth pages are skipped so a signed-out buyer can
  // get through signup. The redirect repeats until the activation page has
  // run once for the token, since the post-signup flow can navigate over
  // the first attempt; after that a failed activation cannot trap the user.
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/logout")) {
      // A buyer told to sign up with another email logs out first; the next
      // account they sign in with gets brought back to finish activation.
      allowAppsumoRedirect();
    }
    if (leavesActivationAlone(path)) {
      return;
    }
    if (shouldRedirectToActivation()) {
      navigate(APPSUMO_ACTIVATE_PATH, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    // Skip workspace_id parameter handling for org owner routes
    if (currentPath.startsWith('/org/')) {
      return;
    }

    const workspaceId = searchParams.get("workspace_id");
    if (!workspaceId) {
      const workspaceIdLS = getStoredWorkspace();
      if (workspaceIdLS) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("workspace_id", workspaceIdLS);
        setSearchParams(newParams, {
          replace: true,
          state: window.history.state,
        });
      }
    } else {
      storeActiveWorkspace(workspaceId);
    }
  }, [setSearchParams, searchParams]);

  const getLoggedInContainer = (Component) => {
    return (
      <>
        <LoggedIn isView={Component === Views.Content.Write}>
          <ChunkErrorBoundary>
            <Suspense fallback={Loading}>
              <Component />
            </Suspense>
          </ChunkErrorBoundary>
        </LoggedIn>
      </>
    );
  };

  const getAdminContainer = (Component) => {
    const AdminWrapper = () => {
      const { notification, hideNotification } =
        React.useContext(NotificationContext);
      return (
        <div className="w-full h-screen overflow-y-auto">
          <Components.Notification
            message={notification.message}
            type={notification.type}
            show={notification.show}
            onClose={hideNotification}
          />
          <ChunkErrorBoundary>
            <Suspense fallback={Loading}>
              <Component />
              <Components.GlobalLoader></Components.GlobalLoader>
            </Suspense>
          </ChunkErrorBoundary>
        </div>
      );
    };
    return <AdminWrapper />;
  };

  const getComponentWithoutNavBar = (Component) => {
    const NotificationWrapper = () => {
      const { notification, hideNotification } =
        React.useContext(NotificationContext);
      return (
        <div className="w-full min-h-full">
          <Components.Notification
            message={notification.message}
            type={notification.type}
            show={notification.show}
            onClose={hideNotification}
          />
          <ChunkErrorBoundary>
            <Suspense fallback={Loading}>
              <Component />
              <Components.GlobalLoader></Components.GlobalLoader>
            </Suspense>
          </ChunkErrorBoundary>
        </div>
      );
    };
    return <NotificationWrapper />;
  };

  const getComponentForAuthPages = (Component) => {
    return (
      <div className="h-full font-sans antialiased text-gray-900 bg-white">
        <ChunkErrorBoundary>
          <Suspense fallback={Loading}>
            <Component />
            <Components.GlobalLoader></Components.GlobalLoader>
          </Suspense>
        </ChunkErrorBoundary>
      </div>
    );
  };

  const getComponentForPublicPages = (Component) => {
    return (
      <div className="h-screen flex flex-col">
        <div className="relative flex flex-grow h-[calc(100%-60px)] pb-[60px] lg:pb-0">
          <main
            className={
              "flex-1 min-w-0 overflow-y-auto bg-white lg:border-l lg:border-gray-200"
            }
          >
            <ChunkErrorBoundary>
              <Suspense fallback={Loading}>
                <Component />
                <Components.GlobalLoader />
              </Suspense>
            </ChunkErrorBoundary>
          </main>
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    Mixpanel.trackPageView();
  }, [navigate]);

  return (
    <Routes>
      <Route
        path="/templates"
        exact="true"
        element={getLoggedInContainer(Views.Content.Templates)}
      />
      <Route
        path="/write"
        exact="true"
        element={getLoggedInContainer(Views.Content.Write)}
      />
      <Route
        path="/schedule"
        exact="true"
        element={getLoggedInContainer(Views.Calendar)}
      />
      <Route
        path="/calendar"
        exact="true"
        element={getLoggedInContainer(Views.Calendar)}
      />
<Route
        path="/kanban"
        exact="true"
        element={getLoggedInContainer(Views.Kanban)}
      />
      <Route
        path="/drafts"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Drafts)}
      />
      <Route
        path="/posts/drafts"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Drafts)}
      />
      <Route
        path="/posts/published"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Published)}
      />
      <Route
        path="/posts/scheduled"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Scheduled)}
      />
      <Route
        path="/posts/failed"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Failed)}
      />
      <Route
        path="/posts/in-progress"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.InProgress)}
      />
      <Route
        path="post/:postid/review/:reviewid"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Review)}
      />
      <Route
        path="/influencers"
        exact="true"
        element={getLoggedInContainer(Views.Content.Influencers)}
      />

      <Route
        path="/viral-posts"
        exact="true"
        element={getLoggedInContainer(Views.Content.ViralPosts)}
      />

      <Route
        path="/influencers/:id"
        exact="true"
        element={getLoggedInContainer(Views.Content.InfluencerPosts)}
      />
      <Route
        path="/posts-for-you"
        exact="true"
        element={getLoggedInContainer(Views.Content.PostsForYou)}
      />
      <Route
        path="/ai"
        exact="true"
        element={getLoggedInContainer(Views.Content.AI)}
      />
      <Route
        path="/"
        exact="true"
        element={getLoggedInContainer(Views.Dashboard)}
      />

      <Route
        path="/voice-notes/*"
        exact="true"
        element={getLoggedInContainer(Views.Content.VoiceNoteToPost)}
      />
      <Route
        path="/ai/ideas-generator"
        exact="true"
        element={getLoggedInContainer(Views.Content.IdeasGenerator)}
      />
      <Route
        path="/ai/ideas-generator/saved"
        exact="true"
        element={getLoggedInContainer(Views.Content.SavedIdeas)}
      />
      <Route
        path="/ai/templates/:id"
        exact="true"
        element={getLoggedInContainer(
          Views.Content.PostGenerationFromTemplates
        )}
      />

      <Route
        path="/creative-studio"
        exact="true"
        element={getLoggedInContainer(Views.WorkspaceCreativeStudio)}
      />

      <Route
        path="/ai/carousels"
        exact="true"
        element={getLoggedInContainer(Views.Content.Carousels.List)}
      />
      <Route
        path="/ai/carousels/saved"
        exact="true"
        element={getLoggedInContainer(Views.Content.Carousels.Saved)}
      />
      <Route
        path="/ai/carousels/text-to-carousel"
        exact="true"
        element={getLoggedInContainer(Views.Content.Carousels.TextToCarousel)}
      />
      <Route
        path="/ai/carousels/carousel-generator"
        exact="true"
        element={getLoggedInContainer(
          Views.Content.Carousels.CarouselGenerator
        )}
      />
      <Route
        path="/ai/carousels/generate-with-ai"
        exact="true"
        element={getComponentWithoutNavBar(
          Views.Content.Carousels.GenerateWithAISelection
        )}
      />
      <Route
        path="/ai/carousels/:id"
        exact="true"
        element={getComponentWithoutNavBar(Views.Content.Carousels.Editor)}
      />

      <Route
        path="/ai/templates/editor"
        exact="true"
        element={getLoggedInContainer(Views.Content.TemplateEditor)}
      />

      <Route
        path="/published-posts"
        exact="true"
        element={getLoggedInContainer(Views.Content.Posts.Published)}
      />

<Route
        path="/login"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.LoginV2)}
      />

      <Route
        path="/oauth/consent"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.OAuthConsent)}
      />
      <Route
        path="/auth/linkedin/callback"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.AuthCallback)}
      />
      <Route
        path="/auth/email/callback"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.AuthCallback)}
      />
      <Route
        path="/auth/google/callback"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.AuthCallback)}
      />
      <Route
        path="/auth/email/update-password"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.UpdatePassword)}
      />
      <Route
        path="/auth/email/forgot-password"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.ForgotPassword)}
      />
      <Route
        path="/auth/email/update-password/accept-invitation"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.AcceptInvitation)}
      />
      <Route
        path="/auth/team-invitation/signup"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.NewTeamMemberSignup)}
      />

      <Route
        path="/signup"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.SignupV2)}
      />

      <Route
        path="/confirm-signup"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.ConfirmSignup)}
      />
      <Route
        path="/org/*"
        element={getComponentWithoutNavBar(Views.Org)}
      />
      <Route
        path="/logout"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.Logout)}
      />
      <Route
        path="/blocked"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.Blocked)}
      />
      <Route
        path="/rate-limited"
        exact="true"
        element={getComponentForAuthPages(Views.Auth.RateLimited)}
      />

      <Route
        path="/payments"
        exact="true"
        element={getLoggedInContainer(Views.Payments.Pricing)}
      />
      <Route
        path="/payments/trial-expired"
        exact="true"
        element={getLoggedInContainer(Views.Payments.TrialExpired)}
      />
      <Route
        path="/payments/trial-expired-analytics"
        exact="true"
        element={getLoggedInContainer(Views.Payments.TrialExpiredModal)}
      />
      <Route
        path="/payments/plan-expired"
        exact="true"
        element={getLoggedInContainer(Views.Payments.PlanExpired)}
      />
      <Route
        path="/payments/plans/lifetime-deal"
        exact="true"
        element={getLoggedInContainer(Views.Payments.CodeRedemption)}
      />
      <Route
        path="/payments/redeem-code"
        exact="true"
        element={getLoggedInContainer(Views.Payments.CodeRedemption)}
      />

      <Route
        path="/integrations/linkedin"
        exact="true"
        element={getLoggedInContainer(
          Views.Integrations.LinkedIn.LinkedInIntegrations
        )}
      />
      <Route
        exact
        path="/integrations/linkedin/callback"
        element={getComponentForAuthPages(LinkedInCallbackHandler)}
      />

      <Route
        path="/chat"
        exact="true"
        element={getLoggedInContainer(Views.Content.Chat)}
      />

      <Route
        path="/workspace/*"
        exact="true"
        element={getLoggedInContainer(Views.Workspace)}
      />
      <Route
        path="/account/*"
        exact="true"
        element={getLoggedInContainer(Views.Account)}
      />

      <Route path="/admin/*" element={getAdminContainer(AdminRoutes)} />
      <Route
        path="/engage/*"
        element={getLoggedInContainer(Views.EngageRoutes)}
      />
      <Route
        path="/public/*"
        element={getComponentForPublicPages(Views.PublicRoutes)}
      />

      <Route
        path="/onboarding"
        exact="true"
        element={getComponentWithoutNavBar(Views.Onboarding)}
      />

      <Route
        path="/onboarding/interview"
        exact="true"
        element={getComponentWithoutNavBar(Views.OnboardingInterview)}
      />

      <Route
        path="/individual"
        exact="true"
        element={getComponentWithoutNavBar(Views.Individual)}
      />

      <Route
        path="/analytics"
        exact="true"
        element={getLoggedInContainer(Views.Analytics)}
      />

      <Route
        path="/post-cast/"
        element={getLoggedInContainer(
          Views.Content.PostCast.PostCastSessionsList
        )}
      />
      <Route
        path="/post-cast/:sessionId"
        element={getComponentWithoutNavBar(
          Views.Content.PostCast.PostCastSessionDetails
        )}
      />
      <Route
        path="/post-cast/:sessionId/analysis"
        exact="true"
        element={getComponentWithoutNavBar(Views.Content.PostCast.SessionAnalysis)}
      />
      <Route
        path="/post-cast/:sessionId/generated"
        exact="true"
        element={getComponentWithoutNavBar(Views.Content.PostCast.PostsView)}
      />
      <Route
        path="/post-cast/:sessionId/previous"
        exact="true"
        element={getComponentWithoutNavBar(Views.Content.PostCast.PostsView)}
      />
      <Route
        path="/knowledge-base"
        exact="true"
        element={getLoggedInContainer(Views.KnowledgeBase)}
      />

      <Route
        path="/challenges/*"
        element={getLoggedInContainer(Views.UserChallenges)}
      />

      <Route
        path="/mcp"
        exact="true"
        element={getLoggedInContainer(Views.Mcp)}
      />
      <Route
        path="/content-radar/*"
        element={
          isContentRadarHost()
            ? getLoggedInContainer(Views.ContentRadarRoutes)
            : getComponentWithoutNavBar(Views.NotFound)
        }
      />
      <Route
        path="/mcp/live"
        element={<Navigate to="/mcp" replace />}
      />

      <Route path="/trial-start" element={<Views.TrialStart />} />

      {/* Landing page from AppSumo's OAuth redirect. Handles its own auth check so
          a new buyer can be sent to the team signup first. */}
      <Route
        path="/appsumo/activate"
        element={getComponentWithoutNavBar(Views.AppsumoActivate)}
      />

      <Route path="*" element={getComponentWithoutNavBar(Views.NotFound)} />
    </Routes>
  );
};

export default App;
